import { useCallback, useMemo, useState } from "react";
import { useStore } from "zustand";

import { store } from "@app/store";
import { useVideo, useVideoTemplate } from "@app/hooks/videos";
import { useUpdateAssets } from "@app/hooks/videoWizard";
import { getPrimaryReferenceKey } from "@app/views/projects/wizardSteps";

import {
  resolveFallbackAsset,
  resolveReferenceAssets,
  resolveVariation,
  resolveVariationAsset,
} from "./resolve";
import { usePersistImageVariations } from "./usePersistImageVariations";
import { isStaticPrompt, resolveStaticPrompt } from "./variationRequest";
import { getVariationType } from "./variationTypes";

/**
 * A single image variation: what it is, what it generated into, and the edits
 * that send it back through the pipeline.
 *
 * This is the counterpart of `useImageVariations` for a view that works on one
 * variation — the edit dialog. It deliberately does *not* run a pipeline of its
 * own: generation is driven by the wizard's `useImageVariations`, and the
 * actions here only change the data that pipeline reacts to (clearing the
 * variation's assets makes it regenerate; clearing its `visualPrompt` makes the
 * prompt regenerate). Every `imageVariations` write goes through the same
 * queued writer as the pipeline's own, so an edit can never drop a prompt
 * persisted in the meantime.
 *
 * @param {object} options
 * @param {string} options.projectId - The video/project the variation is in.
 * @param {string} options.variationKey - The variation's key.
 * @param {string|Array<string>} [options.referenceKey] - The image(s) it is
 *   generated from, as configured — a single key or a list, primary first.
 * @param {object} [options.video] - The video query, when the caller already
 *   has one; otherwise it is loaded here.
 * @param {object} [options.templateData] - The resolved template document,
 *   when the caller already has one; otherwise it is loaded here.
 */
export const useImageVariation = ({
  projectId,
  variationKey,
  referenceKey,
  video,
  templateData,
}) => {
  // Both queries are cached per project/template, so loading them here costs
  // nothing when the caller passes its own.
  const ownVideo = useVideo(projectId);
  const videoQuery = video ?? ownVideo;
  const videoData = videoQuery?.data;

  const ownTemplate = useVideoTemplate(videoData?.templateId);
  const template = templateData ?? ownTemplate.data;

  const updateAssets = useUpdateAssets(projectId);
  const writer = usePersistImageVariations(projectId);
  const { persist } = writer;

  const variation = useMemo(
    () => resolveVariation(template, videoData, variationKey),
    [template, videoData, variationKey],
  );

  const asset = useMemo(
    () => resolveVariationAsset(template, videoData, variationKey),
    [template, videoData, variationKey],
  );

  const [skippedOverride, setSkippedOverride] = useState(null);

  // The switch reacts before its write lands, and so does everything derived
  // from it — the dialog shows the fallback image as soon as it is flipped.
  const isSkipped = skippedOverride ?? !!asset?.isSkipped;

  const fallbackAsset = useMemo(
    () => resolveFallbackAsset(videoData, variation, { ...asset, isSkipped }),
    [videoData, variation, asset, isSkipped],
  );

  // The images this variation is generated from. `referenceKey` is passed in
  // rather than read from the asset: a generated asset's `referenceKey` points
  // at the record it aliases, not at the image it was made from.
  const referenceAssets = useMemo(
    () => resolveReferenceAssets(videoData?.assets, { referenceKey }),
    [videoData?.assets, referenceKey],
  );

  const primaryReference = referenceAssets[0] ?? null;

  // What a static prompt is written from; the same context the pipeline
  // resolves it with.
  const { isStatic, staticPrompt } = useMemo(() => {
    const templateVariation = template?.imageVariations?.find(
      (v) => v.key === variationKey,
    );
    const context = {
      templateId: videoData?.templateId,
      campaign:
        videoData?.campaigns?.find(
          (c) => c.id === videoData?.selectedCampaignIds?.[0],
        ) ?? {},
      customer: videoData?.customer,
      visual: referenceAssets[0],
      visuals: referenceAssets,
    };
    const prompt = resolveStaticPrompt(templateVariation, context);

    // An action's prompt is its label, and an unresolvable one is no default
    // to fall back on.
    return {
      isStatic:
        !!templateVariation &&
        !getVariationType(templateVariation.type)?.action &&
        prompt !== null &&
        isStaticPrompt(templateVariation, context),
      staticPrompt: prompt,
    };
  }, [template, videoData, variationKey, referenceAssets]);

  // Subscribed for this key alone — generation progress ticks often, and a
  // dialog only ever shows one variation.
  const progress = useStore(
    store,
    (state) => state.imageVariationProgress[variationKey],
  );

  /** Switches the variation over to its `fallbackKey` asset, or back. */
  const setSkipped = useCallback(
    (value) => {
      setSkippedOverride(value);

      updateAssets.mutate(
        {
          assets: [
            {
              ...(videoData?.assets?.find((a) => a.name === variationKey) ?? {
                name: variationKey,
              }),
              isSkipped: value,
            },
          ],
        },
        {
          onSettled: () => setSkippedOverride(null),
        },
      );
    },
    [updateAssets.mutate, videoData?.assets, variationKey],
  );

  /**
   * The variation's persisted record, or — when it has none yet, because no
   * prompt was ever generated for it — a seed carrying the identity the
   * generation needs: without `type` the payload switch cannot route it.
   */
  const persistedRecord = useCallback(
    () =>
      videoData?.imageVariations?.find((v) => v.key === variationKey) ?? {
        key: variationKey,
        type: variation?.type,
        referenceKey:
          getPrimaryReferenceKey({ referenceKey }) ?? variation?.referenceKey,
      },
    [
      videoData?.imageVariations,
      variationKey,
      variation?.type,
      variation?.referenceKey,
      referenceKey,
    ],
  );

  /**
   * Regenerates the variation from `prompt`: the prompt is persisted (and this
   * variation's previous failure cleared), then its images are dropped so the
   * wizard's pipeline generates it again.
   *
   * A `prompt` other than the stored one is the user's own: the record is
   * marked `promptEdited`, which keeps the pipeline from refreshing it (see
   * `useImageVariations`) until a prompt is written for it again. For a static
   * variation the default is the static prompt rather than the stored one, and
   * a blank `prompt` stands for it.
   *
   * Both of the variation's assets go: the one named after it, and the
   * generation record it aliases (`firefly__<key>__<uuid>` /
   * `photoshop__<key>__<uuid>`), which `asset.referenceKey` points at.
   */
  const regenerate = useCallback(
    async ({ prompt } = {}) => {
      const { errorCode, errorMessage, ...record } = persistedRecord();

      // Without a `prompt` the stored one stands, edited or not. A static
      // variation's is edited unless the prompt is blank (or is the default).
      const hasPrompt = typeof prompt === "string";
      const isDefault =
        isStatic &&
        hasPrompt &&
        (!prompt.trim() || prompt.trim() === staticPrompt?.trim());
      const isEdited = hasPrompt
        ? isStatic
          ? !isDefault
          : prompt !== (record.visualPrompt ?? "")
        : false;

      if (isDefault) {
        delete record.promptEdited;
      }

      try {
        await persist([
          {
            ...record,
            key: variationKey,
            ...(hasPrompt
              ? { visualPrompt: isDefault ? staticPrompt : prompt }
              : {}),
            ...(isEdited ? { promptEdited: true } : {}),
          },
        ]);
      } catch (error) {
        // The prompt never landed, so keep the current image rather than
        // dropping it for a generation that would run with the old prompt.
        console.warn("Failed to save the edited prompt:", error);
        return;
      }

      updateAssets.mutate({
        toRemove: [
          { name: asset?.name },
          {
            name: getPrimaryReferenceKey({ referenceKey: asset?.referenceKey }),
          },
        ],
      });
    },
    [
      persist,
      persistedRecord,
      updateAssets.mutate,
      variationKey,
      asset?.name,
      asset?.referenceKey,
      isStatic,
      staticPrompt,
    ],
  );

  /**
   * Puts a static variation's default prompt back in place of the user's own:
   * `promptEdited` is cleared and the image is left as it is.
   */
  const resetPrompt = useCallback(() => {
    const { promptEdited, ...record } = persistedRecord();

    return persist([
      { ...record, key: variationKey, visualPrompt: staticPrompt },
    ]).catch((error) => {
      console.warn("Failed to reset the prompt:", error);
    });
  }, [persist, persistedRecord, variationKey, staticPrompt]);

  /**
   * Drops the generated prompt, which is what makes `useImagePrompts` write a
   * fresh one for this variation.
   */
  const regeneratePrompt = useCallback(() => {
    // A prompt written for it is no longer the user's own.
    const { promptEdited, ...record } = persistedRecord();

    return persist([{ ...record, visualPrompt: null }]).catch((error) => {
      console.warn("Failed to request a new prompt:", error);
    });
  }, [persist, persistedRecord]);

  /**
   * Regenerates both the prompt and the image — for when what the prompt was
   * written from (the campaign) has changed: the prompt is dropped (and a
   * previous failure cleared) so `useImagePrompts` writes a fresh one, then
   * the images are dropped so the pipeline generates from it.
   *
   * One write rather than `regeneratePrompt()` followed by `regenerate()`: the
   * latter reads the record from this render's video, so it would put the old
   * prompt back.
   */
  const regenerateFromCampaign = useCallback(async () => {
    // A prompt written for it is no longer the user's own.
    const { errorCode, errorMessage, promptEdited, ...record } =
      persistedRecord();

    try {
      await persist([{ ...record, key: variationKey, visualPrompt: null }]);
    } catch (error) {
      console.warn("Failed to request a new prompt:", error);
      return;
    }

    const toRemove = [
      asset?.name,
      getPrimaryReferenceKey({ referenceKey: asset?.referenceKey }),
    ]
      .filter(Boolean)
      .map((name) => ({ name }));

    // Without an image there is nothing to drop — the fresh prompt alone makes
    // the pipeline generate it.
    if (toRemove.length) {
      updateAssets.mutate({ toRemove });
    }
  }, [
    persist,
    persistedRecord,
    updateAssets.mutate,
    variationKey,
    asset?.name,
    asset?.referenceKey,
  ]);

  return useMemo(
    () => ({
      video: videoQuery,
      template,
      variation,
      asset,
      fallbackAsset,
      referenceAssets,
      primaryReference,
      progress,
      isGenerating: !!progress,
      // A `null` prompt is the "generate me a prompt" marker the prompts hook
      // picks up, so the variation is waiting for one.
      isPromptPending: variation?.visualPrompt === null,
      // The user's own prompt, which is kept rather than refreshed.
      isPromptEdited: !!variation?.promptEdited,
      // Its prompt is the type's own, not written by an LLM — see `resetPrompt`.
      isStatic,
      staticPrompt,
      isBusy: writer.isPending || updateAssets.isPending,
      isSkipped,
      setSkipped,
      regenerate,
      regeneratePrompt,
      resetPrompt,
      regenerateFromCampaign,
    }),
    [
      videoQuery,
      template,
      variation,
      asset,
      fallbackAsset,
      referenceAssets,
      primaryReference,
      progress,
      writer.isPending,
      updateAssets.isPending,
      isSkipped,
      setSkipped,
      isStatic,
      staticPrompt,
      regenerate,
      regeneratePrompt,
      resetPrompt,
      regenerateFromCampaign,
    ],
  );
};
